/**
 * Server-searched option lists for single-record pickers.
 *
 * Each hook owns a search term, debounces it, asks the API for the first
 * page that matches, and resolves the currently selected record by id when
 * it is not on that page (an edit whose record sorts past the first page,
 * a default id from the opening context, or a search that narrowed it
 * away). The result plugs straight into `SearchableRecordSelect` via
 * `pickerProps`, so no list is ever capped by a page size.
 */
import type { SearchableRecordOption } from '@/components/shared/SearchableRecordSelect'
import type { EntityFilters } from '@/store/entity-filters.store'
import type { QueryKey } from '@tanstack/react-query'
import { useState } from 'react'
import { dealApi } from '@/lib/api/deals.api'
import { entityApi } from '@/lib/api/entities.api'
import { projectsApi } from '@/lib/api/projects.api'
import { scopedProjectKey } from '@/lib/realtime/project-query-scope'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueries } from '@tanstack/react-query'

import type { Deal, Entity, ProjectWithEntity } from '@scrambl/shared'

import { useDealQuery, useDealsQuery } from './queries/useDealsQuery'
import { useEntitiesQuery, useEntityQuery } from './queries/useEntitiesQuery'
import { useProjectQuery, useProjectsQuery } from './queries/useProjectsQuery'
import { useTaskQuery, useTasksQuery } from './queries/useTasksQuery'
import { queryKeys } from './query-keys'
import { useDebounceValue } from './useSearchDropdown'
import { useWorkspaceSession } from './useWorkspaceSession'

/** Rows per page; the search narrows beyond it. */
export const RECORD_PICKER_PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 250

/** Build a ScQL free-text clause, escaping the quote and backslash the tokenizer treats specially. */
export function toScqlTextQuery(term: string): string {
  return `text ~ "${term.replace(/[\\"]/g, match => `\\${match}`)}"`
}

export interface RecordPickerArgs {
  /** The currently selected id, resolved by detail fetch when off-page. */
  selectedId?: string | null
  /** Gate the queries (e.g. on a dialog's `open`). Defaults to true. */
  enabled?: boolean
  /** Ids to hide from the list (e.g. the record being moved from). */
  excludeIds?: readonly string[]
}

export interface RecordPickerResult {
  search: string
  setSearch: (search: string) => void
  /** True while the debounce is pending or a request is in flight. */
  isSearching: boolean
  /** The current page plus the selected record pinned first when off-page. */
  options: SearchableRecordOption[]
  selectedOption: SearchableRecordOption | null
  /** Spread onto `SearchableRecordSelect`. */
  pickerProps: {
    options: SearchableRecordOption[]
    search: string
    onSearchChange: (search: string) => void
    isSearching: boolean
  }
}

function usePickerSearch() {
  const [search, setSearch] = useState('')
  const term = useDebounceValue(search.trim(), SEARCH_DEBOUNCE_MS)
  return { search, setSearch, term, isDebouncing: search.trim() !== term }
}

function buildResult(args: {
  search: string
  setSearch: (search: string) => void
  isSearching: boolean
  page: SearchableRecordOption[]
  selectedId?: string | null
  selected: SearchableRecordOption | null
  excludeIds?: readonly string[]
}): RecordPickerResult {
  const { search, setSearch, isSearching, selectedId, selected } = args
  const excluded = new Set(args.excludeIds ?? [])
  const page = excluded.size
    ? args.page.filter(option => !excluded.has(option.value))
    : args.page
  const onPage = selectedId
    ? page.find(option => option.value === selectedId)
    : undefined
  const options = selectedId && !onPage && selected ? [selected, ...page] : page
  const selectedOption = onPage ?? (selectedId ? selected : null)

  return {
    search,
    setSearch,
    isSearching,
    options,
    selectedOption,
    pickerProps: { options, search, onSearchChange: setSearch, isSearching },
  }
}

/* ---------------------------------------------------------------- projects */

type ProjectPickerSource = Pick<
  ProjectWithEntity,
  'id' | 'name' | 'code' | 'entityName' | 'moduleName'
>

export function toProjectOption(
  project: ProjectPickerSource,
): SearchableRecordOption {
  const clientName = project.entityName || project.moduleName || null
  const description = [project.code, clientName].filter(Boolean).join(' · ')

  return {
    value: project.id,
    label: project.name || 'Untitled project',
    description: description || null,
    searchText: [project.name, project.code, clientName]
      .filter(Boolean)
      .join(' '),
  }
}

export function useProjectPicker(
  args: RecordPickerArgs & {
    /** Limit to one client's projects (either link path, server-side). */
    entityId?: string | null
    /** Extra list filters forwarded to the projects query (e.g. status). */
    filters?: Record<string, unknown>
  } = {},
): RecordPickerResult {
  const { selectedId, enabled = true, excludeIds, entityId, filters } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useProjectsQuery(
    {
      ...filters,
      limit: RECORD_PICKER_PAGE_SIZE,
      includeCustomFields: false,
      sortBy: 'name',
      sortOrder: 'asc',
      entityId: entityId || undefined,
      // ScQL `text` matches name, code, description and client name.
      query: term ? toScqlTextQuery(term) : undefined,
    },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(toProjectOption)
  const onPage =
    !!selectedId && page.some(option => option.value === selectedId)
  const detail = useProjectQuery(selectedId ?? undefined, {
    includeCustomFields: false,
    enabled: enabled && !!selectedId && !onPage,
  })

  return buildResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedId,
    selected: detail.data ? toProjectOption(detail.data) : null,
    excludeIds,
  })
}

/* ---------------------------------------------------------------- entities */

type EntityPickerSource = Pick<Entity, 'id' | 'name'>

export function toEntityOption(
  entity: EntityPickerSource,
  fallbackLabel = 'Untitled client',
): SearchableRecordOption {
  return {
    value: entity.id,
    label: entity.name || fallbackLabel,
    searchText: entity.name || undefined,
  }
}

export function useEntityPicker(
  args: RecordPickerArgs & {
    /** Label for a record with no name; pass the org's entity term. */
    fallbackLabel?: string
  } = {},
): RecordPickerResult {
  const { selectedId, enabled = true, excludeIds, fallbackLabel } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useEntitiesQuery(
    {
      limit: RECORD_PICKER_PAGE_SIZE,
      sortBy: 'name',
      sortOrder: 'asc',
      name: term || undefined,
      // Pickers only read id and name.
      includeCustomFields: false,
    } as EntityFilters & { includeCustomFields: boolean },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(entity =>
    toEntityOption(entity as EntityPickerSource, fallbackLabel),
  )
  const onPage =
    !!selectedId && page.some(option => option.value === selectedId)
  const detail = useEntityQuery(selectedId ?? undefined, {
    includeCustomFields: false,
    enabled: enabled && !!selectedId && !onPage,
  })

  return buildResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedId,
    selected: detail.data
      ? toEntityOption(detail.data as EntityPickerSource, fallbackLabel)
      : null,
    excludeIds,
  })
}

/* ------------------------------------------------------------------- deals */

type DealPickerSource = Pick<Deal, 'id' | 'name'> & {
  entity?: { name?: string | null } | null
}

export function toDealOption(deal: DealPickerSource): SearchableRecordOption {
  const clientName = deal.entity?.name || null
  return {
    value: deal.id,
    label: deal.name || 'Untitled deal',
    description: clientName,
    searchText: [deal.name, clientName].filter(Boolean).join(' '),
  }
}

export function useDealPicker(args: RecordPickerArgs = {}): RecordPickerResult {
  const { selectedId, enabled = true, excludeIds } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useDealsQuery(
    {
      limit: RECORD_PICKER_PAGE_SIZE,
      sortBy: 'name',
      sortOrder: 'asc',
      name: term || undefined,
    },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(deal =>
    toDealOption(deal as DealPickerSource),
  )
  const onPage =
    !!selectedId && page.some(option => option.value === selectedId)
  const detail = useDealQuery(selectedId ?? undefined, {
    includeCustomFields: false,
    enabled: enabled && !!selectedId && !onPage,
  })

  return buildResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedId,
    selected: detail.data
      ? toDealOption(detail.data as DealPickerSource)
      : null,
    excludeIds,
  })
}

/* ------------------------------------------------------------------- tasks */

interface TaskPickerSource {
  id: string
  title?: string | null
  taskNumber?: string | number | null
  projectName?: string | null
  project?: { name?: string | null; code?: string | null } | null
}

export function toTaskOption(task: TaskPickerSource): SearchableRecordOption {
  const taskNumber = task.taskNumber ? `#${task.taskNumber}` : null
  const projectName =
    task.project?.name || task.projectName || task.project?.code || null
  const title = task.title || 'Untitled task'

  return {
    value: task.id,
    label: taskNumber ? `${taskNumber} - ${title}` : title,
    description: projectName,
    searchText: [taskNumber, title, projectName].filter(Boolean).join(' '),
  }
}

export function useTaskPicker(args: RecordPickerArgs = {}): RecordPickerResult {
  const { selectedId, enabled = true, excludeIds } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useTasksQuery(
    { limit: RECORD_PICKER_PAGE_SIZE, search: term || undefined },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(task =>
    toTaskOption(task as unknown as TaskPickerSource),
  )
  const onPage =
    !!selectedId && page.some(option => option.value === selectedId)
  const detail = useTaskQuery(selectedId ?? undefined, {
    includeCustomFields: false,
    enabled: enabled && !!selectedId && !onPage,
  })

  return buildResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedId,
    selected: detail.data
      ? toTaskOption(detail.data as unknown as TaskPickerSource)
      : null,
    excludeIds,
  })
}

/* ------------------------------------------------------------ multi-select */

export interface MultiRecordPickerArgs {
  /** Every selected id; those not on the current page are resolved by id. */
  selectedIds?: readonly string[]
  /** Gate the queries (e.g. on a dialog's `open`). Defaults to true. */
  enabled?: boolean
}

export interface MultiRecordPickerResult {
  search: string
  setSearch: (search: string) => void
  /** True while the debounce is pending or a request is in flight. */
  isSearching: boolean
  /** The current page plus every selected record not on it, pinned first. */
  options: SearchableRecordOption[]
  /** Options for the selected ids, in selection order (unresolved ids omitted). */
  selectedOptions: SearchableRecordOption[]
  /** Spread onto `MultiSelect` or a `MultiSelectFilterButtonConfig`. */
  pickerProps: {
    options: SearchableRecordOption[]
    search: string
    onSearchChange: (search: string) => void
    isSearching: boolean
  }
}

/**
 * Resolve records the current page does not carry so their chips keep a
 * label. One detail request per missing id, cached under the same keys the
 * detail hooks use.
 */
function useMissingRecords<T>(args: {
  ids: readonly string[]
  enabled: boolean
  queryKey: (id: string) => QueryKey
  fetch: (id: string, signal: AbortSignal) => Promise<T>
  toOption: (record: T) => SearchableRecordOption
}): SearchableRecordOption[] {
  const { ids, enabled, queryKey, fetch, toOption } = args
  const { currentOrganization } = useOrganizationStore()
  const results = useQueries({
    queries: ids.map(id => ({
      queryKey: queryKey(id),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetch(id, signal),
      enabled: enabled && !!currentOrganization?.id,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  })
  return results.flatMap(result =>
    result.data ? [toOption(result.data as T)] : [],
  )
}

function buildMultiResult(args: {
  search: string
  setSearch: (search: string) => void
  isSearching: boolean
  page: SearchableRecordOption[]
  selectedIds: readonly string[]
  resolved: SearchableRecordOption[]
}): MultiRecordPickerResult {
  const { search, setSearch, isSearching, page, selectedIds, resolved } = args
  const onPage = new Set(page.map(option => option.value))
  const pinned = resolved.filter(option => !onPage.has(option.value))
  const options = pinned.length ? [...pinned, ...page] : page
  const byId = new Map(options.map(option => [option.value, option]))
  const selectedOptions = selectedIds.flatMap(id => {
    const option = byId.get(id)
    return option ? [option] : []
  })

  return {
    search,
    setSearch,
    isSearching,
    options,
    selectedOptions,
    pickerProps: { options, search, onSearchChange: setSearch, isSearching },
  }
}

function missingIds(
  selectedIds: readonly string[],
  page: SearchableRecordOption[],
): string[] {
  const onPage = new Set(page.map(option => option.value))
  return selectedIds.filter(id => !onPage.has(id))
}

export function useProjectMultiPicker(
  args: MultiRecordPickerArgs & {
    entityId?: string | null
    filters?: Record<string, unknown>
  } = {},
): MultiRecordPickerResult {
  const { selectedIds = [], enabled = true, entityId, filters } = args
  const scope = useWorkspaceSession()
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useProjectsQuery(
    {
      ...filters,
      limit: RECORD_PICKER_PAGE_SIZE,
      includeCustomFields: false,
      sortBy: 'name',
      sortOrder: 'asc',
      entityId: entityId || undefined,
      query: term ? toScqlTextQuery(term) : undefined,
    },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(toProjectOption)
  const resolved = useMissingRecords({
    ids: missingIds(selectedIds, page),
    enabled: enabled && scope.enabled,
    queryKey: id =>
      scopedProjectKey(
        queryKeys.projects.detail(id, { includeCustomFields: false }),
        scope,
      ),
    fetch: (id, signal) =>
      scope.run(signal, signal =>
        projectsApi.getProject(id, { includeCustomFields: false, signal }),
      ),
    toOption: toProjectOption,
  })

  return buildMultiResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedIds,
    resolved,
  })
}

export function useEntityMultiPicker(
  args: MultiRecordPickerArgs & { fallbackLabel?: string } = {},
): MultiRecordPickerResult {
  const { selectedIds = [], enabled = true, fallbackLabel } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useEntitiesQuery(
    {
      limit: RECORD_PICKER_PAGE_SIZE,
      sortBy: 'name',
      sortOrder: 'asc',
      name: term || undefined,
      includeCustomFields: false,
    } as EntityFilters & { includeCustomFields: boolean },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(entity =>
    toEntityOption(entity as EntityPickerSource, fallbackLabel),
  )
  const resolved = useMissingRecords({
    ids: missingIds(selectedIds, page),
    enabled,
    queryKey: id => queryKeys.entities.detail(id),
    fetch: id => entityApi.getById(id),
    toOption: entity =>
      toEntityOption(entity as EntityPickerSource, fallbackLabel),
  })

  return buildMultiResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedIds,
    resolved,
  })
}

export function useDealMultiPicker(
  args: MultiRecordPickerArgs = {},
): MultiRecordPickerResult {
  const { selectedIds = [], enabled = true } = args
  const { search, setSearch, term, isDebouncing } = usePickerSearch()

  const list = useDealsQuery(
    {
      limit: RECORD_PICKER_PAGE_SIZE,
      sortBy: 'name',
      sortOrder: 'asc',
      name: term || undefined,
    },
    { enabled },
  )
  const page = (list.data?.data ?? []).map(deal =>
    toDealOption(deal as DealPickerSource),
  )
  const resolved = useMissingRecords({
    ids: missingIds(selectedIds, page),
    enabled,
    queryKey: id => queryKeys.deals.detail(id),
    fetch: id => dealApi.getDeal(id),
    toOption: deal => toDealOption(deal as DealPickerSource),
  })

  return buildMultiResult({
    search,
    setSearch,
    isSearching: isDebouncing || list.isFetching,
    page,
    selectedIds,
    resolved,
  })
}
